import { type HTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { transcriptionBadge, type TranscriptionBadgeVariant } from '@/lib/transcription-status';

export const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium leading-none',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-2 text-ink-2 ring-1 ring-inset ring-line',
        accent: 'bg-accent-soft text-accent-text',
        positive: 'bg-positive/12 text-positive',
        caution: 'bg-caution/12 text-caution',
        critical: 'bg-critical/12 text-critical',
        informative: 'bg-informative/12 text-informative',
      },
      size: {
        sm: 'h-5 px-2 text-[11px]',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  }
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

/** Older variant names map onto tones so call sites keep compiling. */
export type LegacyBadgeVariant =
  | 'default' | 'success' | 'error' | 'warning' | 'info' | 'pending' | 'processing' | 'completed' | 'failed' | 'outline';

const LEGACY_TONE: Record<LegacyBadgeVariant, BadgeTone> = {
  default: 'neutral',
  outline: 'neutral',
  success: 'positive',
  completed: 'positive',
  error: 'critical',
  failed: 'critical',
  warning: 'caution',
  pending: 'caution',
  info: 'informative',
  processing: 'informative',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  variant?: LegacyBadgeVariant;
  /** Render a 6px status dot before the label. */
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, variant, size, dot, children, ...props }, ref) => {
    const resolved = tone ?? (variant ? LEGACY_TONE[variant] : 'neutral');

    return (
      <span ref={ref} className={cn(badgeVariants({ tone: resolved, size }), className)} {...props}>
        {dot ? <span className="size-1.5 rounded-full bg-current" aria-hidden="true" /> : null}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

const STATUS_TONE: Record<TranscriptionBadgeVariant, BadgeTone> = {
  completed: 'positive',
  processing: 'informative',
  failed: 'critical',
  pending: 'caution',
  warning: 'caution',
};

/** The one way to render a transcription status. */
export function StatusBadge({
  transcription,
  size = 'sm',
  className,
}: {
  transcription: Parameters<typeof transcriptionBadge>[0];
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { label, variant } = transcriptionBadge(transcription);

  return (
    <Badge tone={STATUS_TONE[variant]} size={size} dot className={cn('capitalize', className)}>
      {label}
    </Badge>
  );
}
